import { MarketingLayout } from '@/layouts/MarketingLayout'

export default function Privacy() {
  return (
    <MarketingLayout>
      <div className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-3xl font-bold mb-8">Política de Privacidad</h1>
        <p className="text-muted-foreground mb-8">Última actualización: 27 de junio de 2026</p>

        <div className="prose prose-invert max-w-none space-y-6 text-foreground/90">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Información que recopilamos</h2>
            <p>
              Tippy recopila la siguiente información cuando utilizas nuestro servicio:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Información de cuenta:</strong> nombre, número de teléfono y correo electrónico (opcional).</li>
              <li><strong>Información del negocio:</strong> nombre del negocio, horarios de atención y servicios ofrecidos.</li>
              <li><strong>Datos de clientes:</strong> nombre, teléfono y notas de los clientes que registres en la plataforma.</li>
              <li><strong>Datos de citas:</strong> fecha, hora, servicio y cliente asociado a cada cita.</li>
              <li><strong>Datos del dispositivo:</strong> tokens de notificaciones push para enviar alertas.</li>
              <li><strong>Datos de Google Calendar:</strong> si conectas Google Calendar, identificadores y nombres de calendarios, metadatos de eventos, horarios, disponibilidad y eventos creados o sincronizados por Tippy.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Cómo usamos tu información</h2>
            <p>Usamos tu información para:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Proveer el servicio de agenda y gestión de citas.</li>
              <li>Enviar notificaciones push sobre nuevas citas, recordatorios y confirmaciones de asistencia.</li>
              <li>Facilitar la comunicación entre tu negocio y tus clientes vía WhatsApp.</li>
              <li>Proporcionar estadísticas y métricas de tu negocio usando únicamente datos internos de Tippy, no datos obtenidos desde las APIs de Google.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Compartición de datos</h2>
            <p>
              No vendemos, alquilamos ni compartimos tu información personal con terceros para fines de marketing.
              Compartimos datos únicamente con:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>Proveedores de servicio:</strong> Firebase (notificaciones push y autenticación), servicios de hosting y bases de datos, únicamente para operar y proteger Tippy.</li>
              <li><strong>WhatsApp/Meta:</strong> cuando se envían mensajes a clientes a través de la integración de WhatsApp Business.</li>
              <li><strong>Requerimientos legales:</strong> cuando sea necesario para cumplir con la ley aplicable.</li>
            </ul>
            <p className="mt-2">
              No compartimos, transferimos ni divulgamos datos de Google Calendar con terceros salvo cuando sea necesario
              para prestar la funcionalidad visible de sincronización de calendario solicitada por el usuario, por motivos
              de seguridad, para cumplir la ley o cuando tú lo indiques explícitamente.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Almacenamiento y seguridad</h2>
            <p>
              Tu información se almacena en servidores seguros. Utilizamos encriptación en tránsito (HTTPS/TLS)
              y tokens de autenticación seguros para proteger tu cuenta. Las credenciales de inicio de sesión
              se almacenan de forma segura en el dispositivo usando el Keychain (iOS) o Keystore (Android).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Tus derechos</h2>
            <p>Tienes derecho a:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Acceder a tu información personal.</li>
              <li>Corregir datos inexactos.</li>
              <li>Solicitar la eliminación de tu cuenta y datos asociados.</li>
              <li>Desactivar las notificaciones push desde la configuración de tu dispositivo.</li>
            </ul>
            <p className="mt-2">
              Puedes eliminar tu cuenta directamente desde la app en <strong>Configuración &gt; Cuenta &gt; Eliminar cuenta</strong>.
              Si necesitas ayuda adicional con una solicitud de privacidad, contáctanos en <a href="mailto:emiliano@tippy.com.mx" className="text-primary hover:underline">emiliano@tippy.com.mx</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Datos biométricos</h2>
            <p>
              Tippy ofrece inicio de sesión con Face ID o huella digital como opción de conveniencia.
              Los datos biométricos son procesados localmente en tu dispositivo por el sistema operativo
              y nunca son enviados a nuestros servidores.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Contactos del dispositivo</h2>
            <p>
              Si eliges importar clientes desde tus contactos, Tippy accede a tu lista de contactos
              únicamente para facilitar la importación. Antes de subir cualquier contacto, te pedimos
              confirmación explícita. Solo los contactos que selecciones y confirmes se transmiten a
              nuestros servidores y se almacenan como registros de clientes de tu negocio, incluyendo
              nombre, teléfono y correo electrónico si está disponible.
            </p>
            <p className="mt-2">
              Usamos estos datos únicamente para ayudarte a gestionar clientes, citas y comunicaciones
              dentro de Tippy. No vendemos estos contactos ni los compartimos con terceros para fines de marketing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Datos de Google Calendar</h2>
            <p>
              Si conectas Google Calendar, Tippy accede a la lista de calendarios de tu cuenta para que puedas
              seleccionar qué calendario sincronizar. Tippy también accede a los eventos del calendario seleccionado
              para mostrar horarios ocupados, evitar reservas duplicadas y sincronizar citas.
            </p>
            <p className="mt-2">
              Tippy puede crear, actualizar y eliminar eventos de Google Calendar relacionados con citas gestionadas
              en Tippy. También puede leer cambios en eventos sincronizados para reflejarlos en la agenda de Tippy.
            </p>
            <p className="mt-2">
              Usamos los datos de Google Calendar únicamente para proporcionar la funcionalidad de agenda, sincronización
              de citas y disponibilidad que solicitas dentro de Tippy. No vendemos estos datos, no los usamos para
              publicidad, perfiles de usuario, estadísticas o métricas comerciales, entrenamiento de modelos de IA ni otros
              fines ajenos a la sincronización de calendario solicitada por el usuario.
            </p>
            <p className="mt-2">
              El uso y transferencia de información recibida de las APIs de Google se apega a la Política de Datos de
              Usuario de los Servicios API de Google, incluidos los requisitos de Uso Limitado. Solo personal autorizado
              puede acceder a estos datos cuando sea necesario por motivos de seguridad, depuración técnica, cumplimiento
              legal o para operar la sincronización solicitada por el usuario.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Cambios a esta política</h2>
            <p>
              Podemos actualizar esta política de privacidad ocasionalmente. Te notificaremos de cambios
              significativos a través de la aplicación o por correo electrónico.
            </p>
          </section>

          <section>
            <h2 id="eliminacion" className="text-xl font-semibold mb-3">10. Eliminación de cuenta</h2>
            <p>
              Puedes iniciar la eliminación de tu cuenta directamente desde la app siguiendo estos pasos:
            </p>
            <ol className="list-decimal pl-6 space-y-2 mt-2">
              <li>Abre la app y entra a <strong>Configuración &gt; Cuenta &gt; Eliminar cuenta</strong>.</li>
              <li>Revisa el aviso de confirmación y confirma la eliminación.</li>
              <li>La sesión se cerrará y la cuenta quedará marcada para eliminación inmediatamente dentro de la plataforma.</li>
            </ol>
            <p className="mt-4">
              Si tu cuenta es la única propietaria de un negocio, la app te pedirá confirmar también la eliminación de ese negocio antes de completar el proceso.
            </p>
            <p className="mt-4"><strong>Datos que se eliminan:</strong></p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Información de cuenta (nombre, teléfono, correo)</li>
              <li>Datos del negocio (nombre, horarios, servicios)</li>
              <li>Datos de clientes registrados</li>
              <li>Historial de citas</li>
              <li>Tokens de notificaciones push</li>
              <li>Tokens de conexión, configuraciones de sincronización y datos almacenados de Google Calendar asociados a tu cuenta</li>
            </ul>
            <p className="mt-4"><strong>Datos que se conservan:</strong></p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Registros de facturación y pagos, por un período de 5 años según requerimientos fiscales de México.</li>
            </ul>
            <p className="mt-4">
              Procesaremos tu solicitud en un plazo máximo de 30 días.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Contacto</h2>
            <p>
              Si tienes preguntas sobre esta política de privacidad, contáctanos en:
            </p>
            <ul className="list-none space-y-1 mt-2">
              <li>📧 <a href="mailto:emiliano@tippy.com.mx" className="text-primary hover:underline">emiliano@tippy.com.mx</a></li>
              <li>🌐 <a href="https://tippy.com.mx" className="text-primary hover:underline">tippy.com.mx</a></li>
            </ul>
          </section>
        </div>
      </div>
    </MarketingLayout>
  )
}
